import { FireOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import { Pagination } from 'antd';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDevice } from '../../../hooks/useDevice';
import { getCaseLibrary } from '../api';
import styles from './index.module.scss';

const ALL = 'all';
const PAGE_SIZE = 8;

const formatWords = (n) => {
    if (!n) return '';
    if (n >= 10000) return `${(n / 10000).toFixed(1).replace(/\.0$/, '')} 万`;
    return String(n);
};

/**
 * 首页「优秀案例库」（参照墨得）：分类标签 + 卡片网格，点卡片进入只读阅读页 /case/:id。
 * 数据来自 /ai/case-library/list，一次拉全量后在前端按分类筛选、分页。
 */
export default function CaseLibrary() {
    const { isMobile } = useDevice();
    const location = useLocation();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [category, setCategory] = useState(ALL);
    const [page, setPage] = useState(1);

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getCaseLibrary(ALL);
            setItems(Array.isArray(data?.items) ? data.items : []);
            setCategories(Array.isArray(data?.categories) ? data.categories : []);
        } catch (err) {
            // 网络层的英文报错（Network Error / timeout）对用户没意义，统一成中文提示
            const text = err?.message || '';
            setError(/[一-龥]/.test(text) ? text : '案例加载失败，请稍后重试');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    // 从阅读页「返回案例库」回来：滚到这一块
    useEffect(() => {
        if (loading || location.hash !== '#case-library') return;
        document.getElementById('case-library')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [loading, location.hash]);

    const filtered = useMemo(
        () => (category === ALL ? items : items.filter((item) => item.category === category)),
        [items, category],
    );
    const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const selectCategory = (key) => {
        setCategory(key);
        setPage(1);
    };

    const renderCard = (item, index) => {
        const tags = Array.isArray(item.tags) ? item.tags.slice(0, 3) : [];
        return (
            <Link
                key={item.id}
                to={`/case/${item.id}`}
                className={styles.card}
                style={{ animationDelay: `${index * 40}ms` }}
            >
                <div className={styles.cardHead}>
                    <span className={styles.badge}>{item.categoryLabel || '范文'}</span>
                    {item.hot && (
                        <span className={styles.hot}>
                            <FireOutlined /> 热门
                        </span>
                    )}
                </div>
                <h4 className={styles.cardTitle}>{item.title}</h4>
                {item.summary ? <p className={styles.summary}>{item.summary}</p> : null}
                {tags.length > 0 && (
                    <div className={styles.tags}>
                        {tags.map((tag) => (
                            <span key={tag} className={styles.tag}>
                                {tag}
                            </span>
                        ))}
                    </div>
                )}
                <div className={styles.cardFoot}>
                    <span className={styles.words}>
                        {item.wordCount ? `约 ${formatWords(item.wordCount)} 字` : ''}
                    </span>
                    <span className={styles.read}>
                        阅读全文 <RightOutlined />
                    </span>
                </div>
            </Link>
        );
    };

    const renderBody = () => {
        if (loading) return <div className={styles.state}>案例加载中…</div>;
        if (error) {
            return (
                <div className={styles.state}>
                    <span>{error}</span>
                    <button type="button" className={styles.retry} onClick={load}>
                        <ReloadOutlined /> 重试
                    </button>
                </div>
            );
        }
        if (filtered.length === 0) {
            return <div className={styles.state}>范文正在整理中，敬请期待</div>;
        }
        return (
            <>
                <div className={styles.grid}>{pageItems.map(renderCard)}</div>
                {filtered.length > PAGE_SIZE && (
                    <div className={styles.pager}>
                        <Pagination
                            simple={isMobile}
                            current={page}
                            pageSize={PAGE_SIZE}
                            total={filtered.length}
                            showSizeChanger={false}
                            onChange={(next) => setPage(next)}
                        />
                    </div>
                )}
            </>
        );
    };

    return (
        <section id="case-library" className={styles.section} aria-label="优秀案例库">
            <div className={styles.header}>
                <div>
                    <h2 className={styles.title}>优秀案例库</h2>
                    <p className={styles.subtitle}>真实生成的范文，点开即可阅读全文</p>
                </div>
                {items.length > 0 && (
                    <div className={styles.tabs} role="tablist" aria-label="案例分类">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={category === ALL}
                            className={classNames(styles.tab, { [styles.active]: category === ALL })}
                            onClick={() => selectCategory(ALL)}
                        >
                            全部案例
                            <span className={styles.count}>{items.length}</span>
                        </button>
                        {categories.map((cat) => (
                            <button
                                key={cat.key}
                                type="button"
                                role="tab"
                                aria-selected={category === cat.key}
                                className={classNames(styles.tab, {
                                    [styles.active]: category === cat.key,
                                })}
                                onClick={() => selectCategory(cat.key)}
                            >
                                {cat.label}
                                <span className={styles.count}>{cat.count}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {renderBody()}
        </section>
    );
}
